import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

import { useToast } from "@/hooks/use-toast";
import { usePatchCase, type TestCase } from "@/api/tests-api";

/**
 * Edit a library case's title and brief.
 *
 * The images are not editable here — they are what was uploaded, and
 * replacing them under a case candidates already hold would change what
 * a past sitting is a record of. Add a new case instead.
 *
 * The brief IS editable, and that matters: it is the only text a
 * candidate reads, and a typo in it is the most likely thing a preview
 * turns up. Note that an exam can override it per-exam, and a candidate's
 * sealed paper keeps whatever wording it was given — so editing here
 * reaches candidates assigned from now on, not those already holding it.
 */
export function EditCaseModal({
  testCase,
  onClose,
}: {
  testCase: TestCase | null;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const patch = usePatchCase();

  const [title, setTitle] = useState("");
  const [modality, setModality] = useState("");
  const [bodyPart, setBodyPart] = useState("");
  const [history, setHistory] = useState("");

  // Reload whenever a different case is opened, or the previous edit's
  // draft bleeds into the next one.
  useEffect(() => {
    if (!testCase) return;
    setTitle(testCase.title);
    setModality(testCase.modality ?? "");
    setBodyPart(testCase.body_part ?? "");
    setHistory(testCase.clinical_history ?? "");
  }, [testCase]);

  if (!testCase) return null;

  const save = async () => {
    try {
      await patch.mutateAsync({
        caseId: testCase.id,
        body: {
          title: title.trim(),
          modality: modality.trim() || null,
          body_part: bodyPart.trim() || null,
          clinical_history: history.trim() || null,
        },
      });
      toast({ title: "Case saved" });
      onClose();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Could not save this case";
      toast({ title: detail, variant: "error" });
    }
  };

  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          data-cy="edit-case-modal"
          className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-background-card shadow-theme-lg"
        >
          <div className="flex items-start justify-between gap-3 border-b border-border px-6 pt-5 pb-4">
            <div>
              <Dialog.Title className="text-lg font-semibold text-foreground-heading">
                Edit case
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-xs text-foreground-muted">
                {testCase.assigned_count > 0
                  ? `${testCase.assigned_count} candidate${
                      testCase.assigned_count === 1 ? "" : "s"
                    } already hold this case and keep the wording they were given. Changes reach candidates assigned from now on.`
                  : "The images cannot be changed — only how the case is described."}
              </Dialog.Description>
            </div>
            <Dialog.Close
              data-cy="btn-close-edit-case"
              aria-label="Close"
              className="rounded-md p-1 text-foreground-muted hover:bg-table-row-hover hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>

          <div className="space-y-3 px-6 py-5">
            <label className="sk-field flex flex-col">
              <span className="sk-label">Title</span>
              <input
                data-cy="edit-case-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="sk-input mt-1 w-auto"
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="sk-field flex flex-col">
                <span className="sk-label">Modality</span>
                <input
                  data-cy="edit-case-modality"
                  value={modality}
                  onChange={(e) => setModality(e.target.value)}
                  placeholder="US, CT, MR…"
                  className="sk-input mt-1 w-auto"
                />
              </label>
              <label className="sk-field flex flex-col">
                <span className="sk-label">Body part</span>
                <input
                  data-cy="edit-case-body-part"
                  value={bodyPart}
                  onChange={(e) => setBodyPart(e.target.value)}
                  className="sk-input mt-1 w-auto"
                />
              </label>
            </div>

            <label className="sk-field flex flex-col">
              <span className="sk-label">Clinical history — the only text the candidate reads</span>
              <textarea
                data-cy="edit-case-history"
                value={history}
                onChange={(e) => setHistory(e.target.value)}
                rows={4}
                className="sk-input mt-1 w-auto"
              />
            </label>

            <p className="text-[11px] text-foreground-muted">
              No patient name, MRN or date of birth. These images are shown to
              someone outside the clinic.
            </p>

            <button
              type="button"
              data-cy="btn-save-case"
              disabled={!title.trim() || patch.isPending}
              onClick={() => {
                void save();
              }}
              className="sk-btn sk-btn-primary mt-2"
            >
              Save changes
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
